import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import {
  createInstitutionAddress,
  createProviderAddress,
  deleteAddress,
  getInstitutionAddress,
  getProviderAddress,
  hardDeleteAddress,
  unlinkInstitutionAddress,
  unlinkProviderAddress,
  updateInstitutionAddress,
  updateProviderAddress,
} from "#/api/address.api";
import { AddressInput, AddressRow } from "#/api/address.type";
import { SubmitButton } from "#/components/shared/buttons/submit-button";
import Card from "#/components/shared/card";
import Input from "#/components/shared/input";
import Typography from "#/components/shared/typography";
import { ChangeEvent } from "react";
import { useMedicalRecordRequestDetailContext } from "../context/MedicalRecordRequestDetailContext";

const emptyAddress: AddressInput = {
  street: "",
  city: "",
  state: "",
  zip: "",
};

export function RecipientAddressCard() {
  const { recipientType, recipientId, isEditable } =
    useMedicalRecordRequestDetailContext();
  const [form, setForm] = useState<AddressInput>(emptyAddress);
  const hasRecipient = Boolean(recipientId);

  const addressQuery = useQuery<AddressRow | null>({
    queryKey: ["recipient-address", recipientType, recipientId],
    queryFn: async () => {
      try {
        return recipientType === "institution"
          ? await getInstitutionAddress(recipientId as string)
          : await getProviderAddress(recipientId as string);
      } catch (err: any) {
        if (err?.status === 404) return null;
        throw err;
      }
    },
    enabled: hasRecipient,
  });

  const createMutation = useMutation({
    mutationFn: (body: AddressInput) =>
      recipientType === "institution"
        ? createInstitutionAddress(recipientId as string, body)
        : createProviderAddress(recipientId as string, body),
    onSuccess: async () => {
      await addressQuery.refetch();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (body: AddressInput) =>
      recipientType === "institution"
        ? updateInstitutionAddress(recipientId as string, body)
        : updateProviderAddress(recipientId as string, body),
    onSuccess: async () => {
      await addressQuery.refetch();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAddress(id),
    onSuccess: async () => {
      await addressQuery.refetch();
      setForm(emptyAddress);
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: () =>
      recipientType === "institution"
        ? unlinkInstitutionAddress(recipientId as string)
        : unlinkProviderAddress(recipientId as string),
    onSuccess: async () => {
      await addressQuery.refetch();
      setForm(emptyAddress);
    },
  });

  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) => hardDeleteAddress(id),
    onSuccess: async () => {
      await addressQuery.refetch();
      setForm(emptyAddress);
    },
  });

  const address = addressQuery.data;
  const isLoading =
    addressQuery.isLoading ||
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    unlinkMutation.isPending ||
    hardDeleteMutation.isPending;

  const handleChange =
    (field: keyof AddressInput) => (event: ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

  useEffect(() => {
    if (address) {
      setForm({
        street: address.street ?? "",
        city: address.city ?? "",
        state: address.state ?? "",
        zip: address.zip ?? "",
      });
    }
  }, [address]);

  const handleSave = async () => {
    const body: AddressInput = {
      street: form.street ?? "",
      city: form.city ?? "",
      state: form.state ?? "",
      zip: form.zip ?? "",
    };

    if (address) {
      await updateMutation.mutateAsync(body);
    } else {
      await createMutation.mutateAsync(body);
    }
  };

  const handleUnlink = async () => {
    if (!recipientId) return;
    await unlinkMutation.mutateAsync();
  };

  const handleHardDelete = async () => {
    if (!address?.id) return;
    await hardDeleteMutation.mutateAsync(address.id);
  };

  const resolvedForm = useMemo(() => form, [form]);

  return (
    <Card variant="default" elevation={1}>
      <Card.Header variant="default">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <Typography variant="h4" className="text-terracotta">
              Recipient address
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              Linked to this {recipientType}. Update or add an address for
              fax/email requests.
            </Typography>
          </div>
          {!hasRecipient && (
            <Typography variant="caption" className="text-danger">
              No recipient ID available.
            </Typography>
          )}
        </div>
      </Card.Header>
      <Card.Body className="space-y-3">
        {!hasRecipient ? (
          <Typography variant="body2" className="text-brown-600">
            Unable to load address because no {recipientType} ID is available.
          </Typography>
        ) : addressQuery.isLoading ? (
          <Typography variant="body2" className="text-brown-600">
            Loading address…
          </Typography>
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <Input
                label="Street"
                value={resolvedForm.street ?? ""}
                onChange={handleChange("street")}
                disabled={!isEditable || isLoading}
                fullWidth
              />
              <Input
                label="City"
                value={resolvedForm.city ?? ""}
                onChange={handleChange("city")}
                disabled={!isEditable || isLoading}
                fullWidth
              />
              <Input
                label="State"
                value={resolvedForm.state ?? ""}
                onChange={handleChange("state")}
                disabled={!isEditable || isLoading}
                fullWidth
              />
              <Input
                label="ZIP"
                value={resolvedForm.zip ?? ""}
                onChange={handleChange("zip")}
                disabled={!isEditable || isLoading}
                fullWidth
              />
            </div>
            {isEditable && (
              <div className="flex flex-wrap items-center gap-2">
                <SubmitButton
                  size="sm"
                  onClick={handleSave}
                  isLoading={isLoading}
                  isDisabled={isLoading}
                >
                  {address ? "Save address" : "Create address"}
                </SubmitButton>
                {address?.id && (
                  <>
                    <SubmitButton
                      size="sm"
                      color="beige"
                      onClick={handleUnlink}
                      isLoading={unlinkMutation.isPending}
                    >
                      Unlink from {recipientType}
                    </SubmitButton>
                    <SubmitButton
                      size="sm"
                      color="terracotta"
                      onClick={handleHardDelete}
                      isLoading={hardDeleteMutation.isPending}
                    >
                      Hard delete address
                    </SubmitButton>
                  </>
                )}
              </div>
            )}
            {!isEditable && (
              <Typography variant="caption" className="text-brown-500">
                Address updates are disabled.
              </Typography>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default RecipientAddressCard;
